<script lang="ts" setup>
import { appLink } from '@@/bits/app_link'
import { dir } from '@@/bits/current_dir'
import device from '@@/bits/device'
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import { asciiSafeStringify } from '@@/bits/json_stringify'
import { navigateTo } from '@@/bits/location'
import { getSafeStorage } from '@@/bits/safe_storage'
import { isRegistered } from '@@/bits/user_model'
import { defineAsyncComponent } from '@@/bits/vue'
import OzCloseButton from '@@/library/v4/components/OzCloseButton.vue'
import OzContainedButton, {
  OzContainedButtonColorScheme,
  OzContainedButtonSizePreset,
} from '@@/library/v4/components/OzContainedButton.vue'
import OzPadletLogo, {
  OzPadletLogoColorScheme,
  OzPadletLogoSizePreset,
} from '@@/library/v4/components/OzPadletLogo.vue'
import { useSurfaceStore } from '@@/pinia/surface'
import { useCommentsStore } from '@@/pinia/surface_comments'
import { useSurfaceGuestStore } from '@@/pinia/surface_guest_store'
import { useSurfaceOnboardingDemoPadletPanelStore } from '@@/pinia/surface_onboarding_demo_padlet_panel_store'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfaceTranslationsStore } from '@@/pinia/surface_translations_store'
import SurfaceActivityIndicator from '@@/vuecomponents/SurfaceActivityIndicator.vue'
import SurfaceHeaderMobile from '@@/vuecomponents/SurfaceHeaderMobile.vue'
import SurfaceTitle from '@@/vuecomponents/SurfaceTitle.vue'
import { useSurfaceButtonSkinBasedTwClasses } from '@@/vuecomposables/surface_button_skin_based_tw_classes'
import { storeToRefs } from 'pinia'
import { computed, nextTick, onMounted, ref, watch } from 'vue'

const SurfaceNav = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceNav.vue'))
const SurfaceSearchBar = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceSearchBar.vue'))
const SurfaceTitleOld = defineAsyncComponent(() => import('@@/vuecomponents/surface_title.vue'))
const SurfacePadletLogoInScreenshotMode = defineAsyncComponent(
  () => import('@@/vuecomponents/SurfacePadletLogoInScreenshotMode.vue'),
)
const SurfaceTranslationBanner = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceTranslationBanner.vue'))

const safeStorage = getSafeStorage()

const surfaceStore = useSurfaceStore()
const {
  xHeader,
  isEmbedded,
  searchBarTop,
  isWhiteboard,
  isMap,
  backgroundColor,
  headerColor,
  backgroundLuminance,
  colorScheme,
  homeUrl,
  hasSidePanelOutsideSurface,
  isReadOnly,
  isSubmissionRequest,
  isDesktopLayout,
  isMobileLayout,
  user,
  skinPrimaryVariantColorAsRGB,
  isSkinColorAvailable,
  isNativeTenant,
  isScreenshotMode,
  isSearchBarInEditMode,
  xLogoInScreenshotMode,
  wallHashid,
  isFrozen,
} = storeToRefs(surfaceStore)
const surfacePostsStore = useSurfacePostsStore()
const surfaceCommentsStore = useCommentsStore()
const { unapprovedPosts, remotePosts } = storeToRefs(surfacePostsStore)
const { unapprovedComments, remoteComments } = storeToRefs(surfaceCommentsStore)
const { canIAdminister } = storeToRefs(useSurfacePermissionsStore())

const surfaceTranslationsStore = useSurfaceTranslationsStore()
const {
  isViewingTranslatedPadlet,
  isDetectingLanguage,
  hasDismissedTranslationBanner,
  xRevertToOriginalBanner,
  xTranslateToUserLocaleBanner,
} = storeToRefs(surfaceTranslationsStore)

const { buttonSkinBasedTailwindClasses } = useSurfaceButtonSkinBasedTwClasses()

const USER_CLOSED_APPROVE_ALL_BUTTON_MAP_KEY = 'userClosedApproveAllButtonMap'
const isInCloseApproveAllButtonMap = (hashid: string) => {
  try {
    const mapString = safeStorage.getItem(USER_CLOSED_APPROVE_ALL_BUTTON_MAP_KEY) ?? '{}'
    const parsedMap = JSON.parse(mapString)
    return Boolean(parsedMap[hashid])
  } catch (err) {
    // if there is issue parsing USER_CLOSED_APPROVE_ALL_BUTTON_MAP_KEY
    // just reset. worst case user just sees button again
    safeStorage.setItem(USER_CLOSED_APPROVE_ALL_BUTTON_MAP_KEY, '{}')
    return false
  }
}

const shouldCloseApproveAllButton = ref(isInCloseApproveAllButtonMap(wallHashid.value))
const isApprovingAllContributions = ref(false)
const numUnapprovedContributions = computed(() => {
  return unapprovedPosts.value.length + unapprovedComments.value.length
})

const unapprovedContributionsString = computed(() => {
  const numPosts = unapprovedPosts.value.length
  const numComments = unapprovedComments.value.length
  if (numPosts > 0 && numComments > 0) {
    if (numPosts === 1) {
      return __('%{numUnapprovedPosts} unapproved post and %{numUnapprovedComments} comments', {
        numUnapprovedPosts: unapprovedPosts.value.length,
        numUnapprovedComments: numComments,
      })
    } else if (numComments === 1) {
      return __('%{numUnapprovedPosts} unapproved posts and %{numUnapprovedComments} comment', {
        numUnapprovedPosts: numPosts,
        numUnapprovedComments: numComments,
      })
    }
    return __('%{numUnapprovedPosts} unapproved posts and %{numUnapprovedComments} comments', {
      numUnapprovedPosts: unapprovedPosts.value.length,
      numUnapprovedComments: numComments,
    })
  } else if (numPosts > 0 && numComments === 0) {
    return __('%{numUnapprovedPosts} unapproved posts', {
      numUnapprovedPosts: unapprovedPosts.value.length,
    })
  } else if (numPosts === 0 && numComments > 0) {
    return __('%{numUnapprovedComments} unapproved comments', {
      numUnapprovedComments: numComments,
    })
  }

  return undefined
})

const xApproveAllButton = computed(
  () =>
    numUnapprovedContributions.value >= 3 &&
    canIAdminister.value &&
    !shouldCloseApproveAllButton.value &&
    !isSearchBarInEditMode.value &&
    !isFrozen.value &&
    !isViewingTranslatedPadlet.value,
)

watch(numUnapprovedContributions, (newVal) => {
  if (newVal < 3) {
    shouldCloseApproveAllButton.value = true
  }
})

watch([remotePosts, remoteComments], () => {
  // we only need to handle remote contribution editing and adding to distinguish from
  // initial fetch for posts/comments since fetching for posts/comments wont reduce numUnapprovedContributions
  if (numUnapprovedContributions.value >= 3) {
    updateUserClosedApproveAllButtonMap(wallHashid.value, false)
    shouldCloseApproveAllButton.value = false
  }
})

const surfaceOnboardingDemoPadletPanelStore = useSurfaceOnboardingDemoPadletPanelStore()
const { demoPadletPanelVisibility, isDemoPadletPanelDesktop } = storeToRefs(surfaceOnboardingDemoPadletPanelStore)

const surfaceGuestStore = useSurfaceGuestStore()

const hasDarkBackgroundLuminance = computed(() => {
  if (isMap.value) {
    return colorScheme.value !== 'light'
  }

  return backgroundLuminance.value !== 'light'
})
const realBackgroundLuminance = computed(() => {
  if (isMap.value) {
    return colorScheme.value
  }
  return backgroundLuminance.value
})

const isUserRegistered = computed(() => {
  return isRegistered(user.value)
})

const approveAllWallContributions = async (): Promise<void> => {
  if (isApprovingAllContributions.value) return
  isApprovingAllContributions.value = true
  await surfaceStore.approveAllContributions({
    wallHashid: wallHashid.value,
    unapprovedPosts: unapprovedPosts.value,
    unapprovedComments: unapprovedComments.value,
  })
  updateUserClosedApproveAllButtonMap(wallHashid.value, false)
  isApprovingAllContributions.value = false
}

// Check if the banner is in the scroll area so that it can be hidden
// Used to hide the banner when the search bar is accessed and the banner is not in the scroll area (e.g. when cmd+f is used)
const isBannerInScrollArea = () => {
  const wallContainer = document.querySelector('#wall-container')
  if (!wallContainer) return true
  return wallContainer?.scrollTop < 52 // 52 is the distance from the bottom of the button to the top of the wall
}

const updateUserClosedApproveAllButtonMap = (hashid: string, value: boolean) => {
  try {
    const mapString = safeStorage.getItem(USER_CLOSED_APPROVE_ALL_BUTTON_MAP_KEY) ?? '{}'
    const parsedMap = JSON.parse(mapString)
    if (value === true) {
      parsedMap[hashid] = value
    } else {
      delete parsedMap[hashid]
    }
    safeStorage.setItem(USER_CLOSED_APPROVE_ALL_BUTTON_MAP_KEY, asciiSafeStringify(parsedMap))
  } catch (err) {
    // if there is issue with updating the map
    // just reset. worst case user just sees button again
    safeStorage.setItem(USER_CLOSED_APPROVE_ALL_BUTTON_MAP_KEY, '{}')
  }
}

const closeApproveAllButton = () => {
  shouldCloseApproveAllButton.value = true
  updateUserClosedApproveAllButtonMap(wallHashid.value, true)
}

const xTranslationBanner = computed<boolean>(
  () =>
    // Only show the banner after the detection of language is completed
    !isDetectingLanguage.value &&
    !hasDismissedTranslationBanner.value &&
    !surfaceStore.isSearchBarInEditMode &&
    (xRevertToOriginalBanner.value || xTranslateToUserLocaleBanner.value) &&
    !xApproveAllButton.value,
)

// Obfuscate the app link
function openAppLink() {
  navigateTo(appLink)
}

onMounted(() => {
  // Don't prompt for notifications if the user is not registered and the app is not the electron app
  if (isUserRegistered.value) {
    surfaceStore.checkAndSubscribeToPushNotifications()
  }
})

const updateHeaderHeight = () => {
  if (isDesktopLayout.value) {
    nextTick(() => {
      const headerEl = surfaceHeader.value
      if (!headerEl) return
      surfaceStore.resizeHeader({ height: headerEl.getBoundingClientRect().height })
    })
  }
}

const surfaceHeaderMobile = ref<InstanceType<typeof SurfaceHeaderMobile>>()
const collapse = (): void => {
  if (!surfaceHeaderMobile.value) return
  surfaceHeaderMobile.value.collapse()
}

const surfaceHeaderDesktop = ref<HTMLDivElement>()
const surfaceHeaderEmbedded = ref<HTMLDivElement>()
const surfaceHeader = computed(
  () =>
    (surfaceHeaderMobile.value?.$el || surfaceHeaderDesktop.value || surfaceHeaderEmbedded.value) as
      | HTMLElement
      | undefined,
)

watch(
  surfaceHeader,
  () => {
    updateHeaderHeight()
  },
  { immediate: true },
)

defineExpose({
  collapse,
})
</script>

<template>
  <SurfaceHeaderMobile v-if="isMobileLayout && !isEmbedded" id="surface-header" ref="surfaceHeaderMobile" />
  <div
    v-else-if="isDesktopLayout && !isEmbedded"
    id="surface-header"
    ref="surfaceHeaderDesktop"
    data-testid="surfaceHeader"
    :dir="dir()"
    :class="['app-bar', 'surface-header', 'relative']"
    :data-luminance="realBackgroundLuminance"
    :style="{ backgroundColor: headerColor }"
  >
    <SurfacePadletLogoInScreenshotMode v-if="isScreenshotMode && xLogoInScreenshotMode" />
    <div v-else :class="['absolute flex flex-row top-4.5 start-5 gap-x-2']">
      <OzPadletLogo
        v-if="xHeader"
        :href="homeUrl"
        :dark-mode="hasDarkBackgroundLuminance"
        :size-preset="OzPadletLogoSizePreset.W48px"
        :color-scheme="OzPadletLogoColorScheme.Text300"
        data-testid="padletLogo"
        @click="surfaceOnboardingDemoPadletPanelStore.onLogoClick()"
      />
      <OzContainedButton
        v-if="
          isAppUsing('openInAppBanner') && !device.app && (device.ios || device.android) && !isDemoPadletPanelDesktop
        "
        class="max-w-39 truncate"
        :style="isSkinColorAvailable ? { backgroundColor: skinPrimaryVariantColorAsRGB } : {}"
        :aria-label="__('Open in Padlet app')"
        :text="__('Open in app')"
        :color-scheme="OzContainedButtonColorScheme.Transparent"
        :size-preset="OzContainedButtonSizePreset.H24px"
        :dark-mode="realBackgroundLuminance === 'dark'"
        data-testid="surfaceOpenInAppButton"
        data-pepin-id="surface-header-tablet-open-in-app"
        data-pepin-trigger="click"
        @click="openAppLink"
      />
    </div>
    <SurfaceActivityIndicator
      :class="[
        'absolute top-4.5',
        // We don't know the width of this component, so to horizontally
        // center it, we set its start to 50% of its parent, and translate
        // it 50% to the opposite direction.
        'start-1/2 -translate-s-1/2',
      ]"
    />

    <SurfaceTitle
      class="mt-9"
      @title-changed="updateHeaderHeight"
      @description-changed="updateHeaderHeight"
      @portrait-changed="updateHeaderHeight"
    />

    <!-- SurfaceHeader's size will change depending on whether the vertical scrollbar appears -->
    <!-- or not. Since we want to keep the search bar's position fixed regardless of that, we -->
    <!-- use `position: fixed` and update its `top` when we scroll. See `surface_container.vue` -->
    <!-- `handlePageScroll` method. -->
    <!-- Distance of search bar from  -->
    <!-- 488px because size of panel is 400px when smaller than tablet portrait -->
    <!-- 568px because size of panel is 480px when smaller than tablet portrait -->
    <div
      v-if="!isReadOnly && !isSubmissionRequest"
      :class="[
        'fixed z-search-bar flex items-center h-10',
        hasSidePanelOutsideSurface ? 'end-22 @desktop/surface-container:end-42' : 'end-22',
      ]"
      :style="{
        top: `${searchBarTop}px`,
      }"
    >
      <div class="flex items-center gap-6">
        <SurfaceTranslationBanner
          v-if="isAppUsing('translatePadlet') && xTranslationBanner && isBannerInScrollArea()"
          :button-classes="buttonSkinBasedTailwindClasses"
          :is-dark-mode="realBackgroundLuminance === 'dark'"
        />
        <Transition
          v-if="xApproveAllButton && isBannerInScrollArea()"
          enter-active-class="transition-opacity duration-200"
          enter-from-class="opacity-0"
          enter-to-class="opacity-100"
          leave-active-class="transition-opacity duration-200"
          leave-from-class="opacity-100"
          leave-to-class="opacity-0"
        >
          <div data-testid="surfaceHeaderApproveAllButtonContainer" class="flex items-center gap-2">
            <OzCloseButton
              :class="[
                'bg-transparent',
                realBackgroundLuminance === 'dark'
                  ? {
                      'text-light-text-200': true,
                      'hhover:bg-button-dark hhover:text-light-text-100': true,
                      'hactive:!bg-button-hover-dark hactive:!text-light-text-100': true,
                    }
                  : {
                      'text-dark-text-200': true,
                      'hhover:bg-button-light hhover:text-dark-text-100': true,
                      'hactive:!bg-button-hover-light hactive:!text-dark-text-100': true,
                    },
              ]"
              :dark-mode="false"
              @click="closeApproveAllButton"
            />
            <p
              :class="['font-sans', realBackgroundLuminance === 'dark' ? 'text-light-text-100' : 'text-dark-text-100']"
              data-testid="surfaceHeaderApproveAllButtonText"
            >
              {{ unapprovedContributionsString }}
            </p>
            <OzContainedButton
              :text="__('Approve all')"
              :color-scheme="OzContainedButtonColorScheme.Primary"
              :size-preset="OzContainedButtonSizePreset.H32px"
              :disabled="isApprovingAllContributions"
              data-testid="surfaceHeaderApproveAllButton"
              @click="approveAllWallContributions"
            />
          </div>
        </Transition>
        <SurfaceSearchBar v-if="!isWhiteboard && demoPadletPanelVisibility.showSurfaceSearchBar" />
      </div>
      <OzContainedButton
        v-if="isAppUsing('signUpSurface') && !isUserRegistered && !surfaceStore.isSimplifiedView"
        :class="[
          'ms-4',

          // Base
          {
            'bg-[rgba(255,255,255,0.32)]': backgroundLuminance === 'light',
            'bg-[rgba(0,0,0,0.24)]': backgroundLuminance === 'dark',
          },
          'backdrop-blur-sm',
          {
            'text-dark-text-200': backgroundLuminance === 'light',
            'text-light-text-200': backgroundLuminance === 'dark',
          },

          // Hover
          {
            'hover-hover:hover:bg-light-ui-100': backgroundLuminance === 'light',
            'hover-hover:hover:bg-dark-ui-100': backgroundLuminance === 'dark',
          },
          {
            'hover-hover:hover:text-dark-text-100': backgroundLuminance === 'light',
            'hover-hover:hover:text-light-text-100': backgroundLuminance === 'dark',
          },
        ]"
        :href="isNativeTenant ? surfaceGuestStore.signupUrl : surfaceGuestStore.loginUrl"
        :data-pepin-id="isNativeTenant ? 'signup-cta-on-surface' : 'login-cta-on-surface'"
        :data-testid="isNativeTenant ? 'surfaceHeaderSignupButton' : 'surfaceHeaderLoginButton'"
        :color-scheme="OzContainedButtonColorScheme.Bare"
        :size-preset="OzContainedButtonSizePreset.H32px"
        :dark-mode="backgroundLuminance === 'dark'"
        :text="isNativeTenant ? __('Sign up') : __('Log in')"
      />
    </div>
  </div>
  <div
    v-else
    id="surface-header"
    ref="surfaceHeaderEmbedded"
    :class="['app-bar', 'surface-header']"
    :data-luminance="realBackgroundLuminance"
    :style="{ backgroundColor: headerColor }"
  >
    <div class="rubber-band-scroll-padder" :style="{ backgroundColor: backgroundColor }" />
    <SurfaceNav v-if="xHeader" />
    <SurfaceTitleOld
      data-testid="surfaceTitle"
      @title-changed="updateHeaderHeight"
      @description-changed="updateHeaderHeight"
    />
  </div>
</template>
